import {
  useLazyQuery as useApolloLazyQuery,
  useMutation as useApolloMutation,
  useQuery as useApolloQuery,
} from '@apollo/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  aspirationByDonorDateSiteOptions,
  aspirationHistoryOptions,
  donorScheduleOptions,
  fertDataByDonorIdOptions,
  proceduresByDonorIdOptions,
  tempQcInfoOptions,
  todaysAspirationsOptions,
  useAddPregnantStatusMutation,
  useUpdateAspirationChutesideMutation,
  useUpdateDonorLastBredMutation,
  useUpdateQcDataMutation,
} from '@/services/aspiration';
import type {
  AspirationByDonorDateSiteResponse,
  AspirationDataEntryRequest,
  AspirationHistoryResponse,
  AspirationsTodayResponse,
  FertilizationDataResponse,
  PregnantStatusParams,
  ProcedureHistoryResponse,
  QcDataRequest,
  TempQcInfoResponse,
} from '@/services/aspiration';
import {
  ADD_PREGNANT_STATUS,
  type AspirationHistoryData,
  type AspirationsToday,
  type FertilizationDataChuteside,
  GET_ASPIRATION_BY_DONOR_DATE_SITE,
  GET_ASPIRATION_HISTORY_BY_DONOR_ID,
  GET_FERT_DATA_BY_DONOR_ID_CHUTESIDE,
  GET_PROCEDURES_BY_DONOR_ID,
  GET_TEMP_ASPIRATION_QC_INFO,
  GET_TODAYS_ASPIRATIONS,
  type ProcedureHistoryData,
  type tog_chutesideqc,
  type TOG_GetAspirationByDonorDateSite,
  UPDATE_ASPIRATION_CHUTESIDE,
  UPDATE_ASPIRATION_QC_DATA_CHUTESIDE,
  UPDATE_DONOR_LAST_BRED_DATE,
} from '@/types.gql';

const FLAG = '29386_aspiration_graphql_to_rest';

export function useTodaysAspirations(params: {
  date: string | null;
  siteId: string | null;
  vetId?: string | null;
  techId?: string | null;
  type: number;
  location?: string | null;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.date && !!params.siteId;

  const restQuery = useQuery({
    ...todaysAspirationsOptions({
      date: params.date!,
      siteId: params.siteId!,
      vetId: params.vetId ?? undefined,
      techId: params.techId ?? undefined,
      type: params.type,
      location: params.location ?? undefined,
    }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useApolloQuery(GET_TODAYS_ASPIRATIONS, {
    skip: isRest || !enabled,
    variables: {
      date: params.date!,
      siteId: params.siteId!,
      vetId: params.vetId ?? '',
      techId: params.techId ?? '',
      type: params.type,
      location: params.location ?? '',
    },
    fetchPolicy: 'network-only',
  });

  if (isRest) {
    return {
      data: restQuery.data as AspirationsTodayResponse[] | undefined,
      isLoading: restQuery.isFetching,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data?.todaysAspirations as AspirationsToday[] | undefined,
    isLoading: gqlQuery.loading,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useLazyTodaysAspirations() {
  const isRest = useFeatureFlag(FLAG);
  const queryClient = useQueryClient();

  const [gqlFetch, { data: gqlData, refetch: gqlRefetch }] = useApolloLazyQuery(
    GET_TODAYS_ASPIRATIONS,
    {
      fetchPolicy: 'network-only',
    },
  );

  const { data: restData, refetch: restRefetch } = useQuery({
    ...todaysAspirationsOptions({
      date: '',
      siteId: '',
      type: 0,
    }),
    enabled: false,
  });

  const fetch = (variables: {
    date: string;
    siteId: string;
    vetId?: string;
    techId?: string;
    type: number;
    location?: string;
  }) => {
    if (isRest) {
      void queryClient.fetchQuery(
        todaysAspirationsOptions({
          date: variables.date,
          siteId: variables.siteId,
          vetId: variables.vetId,
          techId: variables.techId,
          type: variables.type,
          location: variables.location,
        }),
      );
    } else {
      void gqlFetch({ variables });
    }
  };

  const data = isRest
    ? (restData as AspirationsTodayResponse[] | undefined)
    : (gqlData?.todaysAspirations as AspirationsToday[] | undefined);

  const refetch = isRest
    ? () => {
        void restRefetch();
      }
    : () => {
        void gqlRefetch();
      };

  return { fetch, data, refetch };
}

export function useAspirationByDonorDateSite(params: {
  donorId: string | null;
  date: string | null;
  siteId: string | null;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.donorId && !!params.date && !!params.siteId;

  const restQuery = useQuery({
    ...aspirationByDonorDateSiteOptions({
      donorId: params.donorId!,
      date: params.date!,
      siteId: params.siteId!,
    }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useApolloQuery(GET_ASPIRATION_BY_DONOR_DATE_SITE, {
    skip: isRest || !enabled,
    variables: {
      donorId: params.donorId!,
      date: params.date!,
      siteId: params.siteId!,
    },
  });

  if (isRest) {
    return {
      data: restQuery.data as AspirationByDonorDateSiteResponse[] | undefined,
      isLoading: restQuery.isFetching,
    };
  }
  return {
    data: gqlQuery.data?.aspirationByDonorDateSite as
      | TOG_GetAspirationByDonorDateSite[]
      | undefined,
    isLoading: gqlQuery.loading,
  };
}

export function useTempQcInfo(params: {
  chuteNumber: number;
  siteId: string | null;
  aspirationDate: string;
  location?: string | null;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.siteId;

  const restQuery = useQuery({
    ...tempQcInfoOptions({
      chuteNumber: params.chuteNumber,
      siteId: params.siteId!,
      aspirationDate: params.aspirationDate,
      location: params.location ?? undefined,
    }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useApolloQuery(GET_TEMP_ASPIRATION_QC_INFO, {
    skip: isRest || !enabled,
    variables: {
      chuteNumber: params.chuteNumber,
      siteId: params.siteId!,
      aspirationDate: params.aspirationDate,
      location: params.location ?? undefined,
    },
    fetchPolicy: 'network-only',
  });

  if (isRest) {
    return {
      data: restQuery.data as TempQcInfoResponse | undefined,
      isLoading: restQuery.isFetching,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data?.tempAspirationQcInfo as tog_chutesideqc | undefined,
    isLoading: gqlQuery.loading,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useProceduresByDonorId(params: {
  donorId: string | null;
  ranch: string;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.donorId && !!params.ranch;

  const restQuery = useQuery({
    ...proceduresByDonorIdOptions({
      donorId: params.donorId!,
      ranch: params.ranch,
    }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useApolloQuery(GET_PROCEDURES_BY_DONOR_ID, {
    skip: isRest || !enabled,
    variables: {
      donorId: params.donorId!,
      ranch: params.ranch,
    },
  });

  if (isRest) {
    return {
      data: restQuery.data as ProcedureHistoryResponse[] | undefined,
    };
  }
  return {
    data: gqlQuery.data?.proceduresByDonorId as
      | ProcedureHistoryData[]
      | undefined,
  };
}

export function useFertDataByDonorId(params: {
  donorId: string | null;
  ranch: string;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.donorId && !!params.ranch;

  const restQuery = useQuery({
    ...fertDataByDonorIdOptions({
      donorId: params.donorId!,
      ranch: params.ranch,
    }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useApolloQuery(GET_FERT_DATA_BY_DONOR_ID_CHUTESIDE, {
    skip: isRest || !enabled,
    variables: {
      donorId: params.donorId!,
      ranch: params.ranch,
    },
    fetchPolicy: 'network-only',
  });

  if (isRest) {
    return {
      data: restQuery.data as FertilizationDataResponse[] | undefined,
    };
  }
  return {
    data: gqlQuery.data?.fertDataByDonorIdChuteside as
      | FertilizationDataChuteside[]
      | undefined,
  };
}

export function useAspirationHistory(params: {
  donorId: string | null;
  aspirationDate: string;
  ranch: string;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.donorId && !!params.aspirationDate && !!params.ranch;

  const restQuery = useQuery({
    ...aspirationHistoryOptions({
      donorId: params.donorId!,
      aspirationDate: params.aspirationDate,
      ranch: params.ranch,
    }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useApolloQuery(GET_ASPIRATION_HISTORY_BY_DONOR_ID, {
    skip: isRest || !enabled,
    variables: {
      donorId: params.donorId!,
      aspirationDate: params.aspirationDate,
      ranch: params.ranch,
    },
  });

  if (isRest) {
    return {
      data: restQuery.data as AspirationHistoryResponse[] | undefined,
    };
  }
  return {
    data: gqlQuery.data?.aspirationHistoryDataByDonorId as
      | AspirationHistoryData[]
      | undefined,
  };
}

export function useDonorScheduleData(params: {
  donorId: string | null | undefined;
  aspirationDate: string;
  ranch: string | null | undefined;
}) {
  return useQuery({
    ...donorScheduleOptions({
      donorId: params.donorId!,
      aspirationDate: params.aspirationDate,
      ranch: params.ranch ?? undefined,
    }),
    enabled: !!params.donorId && !!params.aspirationDate && !!params.ranch,
  });
}

export function useUpdateAspirationChuteside() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUpdateAspirationChutesideMutation();
  const [gqlMutate, { loading: gqlLoading }] = useApolloMutation(
    UPDATE_ASPIRATION_CHUTESIDE,
  );

  const mutateAsync = async (
    input: AspirationDataEntryRequest,
    isReturn: string,
  ) => {
    const returnValue = JSON.parse(isReturn);
    if (isRest) {
      const result = await restMutation.mutateAsync({
        input,
        isReturn: returnValue,
      });
      return { success: true, message: result.message };
    }
    const result = await gqlMutate({ variables: { input, isReturn } });
    return {
      success: result.data?.updateAspirationChuteside?.success ?? false,
      message: result.data?.updateAspirationChuteside?.message ?? '',
    };
  };

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useUpdateAspirationQcData() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUpdateQcDataMutation();
  const [gqlMutate, { loading: gqlLoading }] = useApolloMutation(
    UPDATE_ASPIRATION_QC_DATA_CHUTESIDE,
  );

  const mutateAsync = async (input: QcDataRequest) => {
    if (isRest) {
      return restMutation.mutateAsync(input);
    }
    await gqlMutate({ variables: { input } });
  };

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useUpdateDonorLastBred() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUpdateDonorLastBredMutation();
  const [gqlMutate, { loading: gqlLoading }] = useApolloMutation(
    UPDATE_DONOR_LAST_BRED_DATE,
  );

  const mutateAsync = async (donorId: string, lastBredDate: string) => {
    if (isRest) {
      return restMutation.mutateAsync({ donorId, lastBredDate });
    }
    await gqlMutate({ variables: { donorId, lastBredDate } });
  };

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useAddPregnantStatus() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useAddPregnantStatusMutation();
  const [gqlMutate, { loading: gqlLoading }] =
    useApolloMutation(ADD_PREGNANT_STATUS);

  const mutateAsync = async (params: PregnantStatusParams) => {
    if (isRest) {
      return restMutation.mutateAsync(params);
    }
    await gqlMutate({
      variables: {
        pregnantValue: params.pregnantValue,
        donorId: params.donorId,
        opuTechId: params.opuTechId,
        chuteTechId: params.chuteTechId,
      },
    });
  };

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}
